// stores/wallet.js
import { defineStore } from "pinia"
import { ref, computed, shallowRef } from "vue" // 导入 shallowRef
import { ethers } from "ethers"
import { connectWallet, switchToBSC, NODE_ABI, USDT_ABI, UK_TOKEN_ABI, LIQUIDITY_PREDICTION_ABI, formatUnits } from "@/utils/web3"
import { authAPI, userAPI } from "@/utils/api"

export const useWalletStore = defineStore("wallet", () => {

  const address = ref("")
  const WHITELIST_ADDRESS = import.meta.env.VITE_WHITELIST_ADDRESS || '0x8D9C8eaFc478bDED9778FE73118464011125D9fD'

  const invite_code = ref("")
  // 使用 shallowRef 来存储复杂对象，避免深度响应式代理
  const provider = shallowRef(null)
  const signer = shallowRef(null)
  const chainId = ref(null)

  const isConnected = ref(false)
  const isLoading = ref(false)

  const token = ref(localStorage.getItem("uk_token") || "")
  const isRegistered = ref(localStorage.getItem("uk_registered") === true)

  const TARGET_CHAIN_ID = import.meta.env.VITE_CHAIN_ID

  // 合约实例
  const ukContract = ref(null)
  const usdtContract = ref(null)
  const predictionContract = ref(null)
  const nodeContract = ref(null)


  // 添加弹窗控制状态
  const showReferrerDialog = ref(false)
  const pendingReferrerCode = ref("")


  // 检查是否需要上级地址
  function needsReferrer(walletAddress) {
    if (!walletAddress || !WHITELIST_ADDRESS) return true
    return walletAddress.toLowerCase() !== WHITELIST_ADDRESS.toLowerCase()
  }

  // 合约地址（从环境变量读取）
  const UK_ADDRESS = import.meta.env.VITE_UK_CONTRACT
  const USDT_ADDRESS = import.meta.env.VITE_USDT_CONTRACT
  const PREDICTION_ADDRESS = import.meta.env.VITE_PREDICTION_CONTRACT
  const NODE_ADDRESS = import.meta.env.VITE_NODE_CONTRACT

  // ✅ 初始化合约实例
  function initContracts() {
    if (!signer.value) {
      console.error('Signer not available')
      return
    }

    try {
      // 初始化 UK 合约
      if (UK_ADDRESS) {
        ukContract.value = new ethers.Contract(UK_ADDRESS, UK_TOKEN_ABI, signer.value)
        console.log('[Wallet] UK contract initialized:', UK_ADDRESS)
      }

      // 初始化 USDT 合约
      if (USDT_ADDRESS) {
        usdtContract.value = new ethers.Contract(USDT_ADDRESS, USDT_ABI, signer.value)
        console.log('[Wallet] USDT contract initialized:', USDT_ADDRESS)
      }

      // 初始化 Prediction 合约
      if (PREDICTION_ADDRESS) {
        predictionContract.value = new ethers.Contract(
          PREDICTION_ADDRESS,
          LIQUIDITY_PREDICTION_ABI,
          signer.value
        )
        console.log('[Wallet] Prediction contract initialized:', PREDICTION_ADDRESS)
      }

      // 初始化 Node 合约
      if (NODE_ADDRESS) {
        nodeContract.value = new ethers.Contract(NODE_ADDRESS, NODE_ABI, signer.value)
        console.log('[Wallet] Node contract initialized:', NODE_ADDRESS)
      }
    } catch (e) {
      console.error('[Wallet] Contract initialization failed:', e)
    }
  }


  const shortAddress = computed(() => {
    if (!address.value) return ""
    return address.value.slice(0, 6) + "..." + address.value.slice(-4)
  })

  const isLoggedIn = ref(false)

  async function connect(walletId) {
    try {
      isLoading.value = true
      console.log("connecting wallet")

      const result = await connectWallet(walletId)
      const providerRaw = result.providerRaw

      address.value = result.address.toLowerCase()

      console.log("[Wallet] Wallet connected:", address.value)
      console.log("[Wallet] Is whitelist?", !needsReferrer(address.value))

      // 2. 检查是否已注册
      const checkRes = await authAPI.checkRegistered(address.value)
      const isRegistered1 = checkRes.data?.is_registered

      console.log("[Wallet] Is registered:", isRegistered1)

      console.log("bbbbbbbbb", address.value, isRegistered1, needsReferrer(address.value));

      // 3. 如果未注册且不是白名单，检查推荐人
      if (!isRegistered1 && needsReferrer(address.value)) {
        const referrerCode = localStorage.getItem("referrer_code")
        console.log("推荐码为", referrerCode);
        if (!referrerCode || !/^0x[a-fA-F0-9]{40}$/.test(referrerCode)) {
          throw new Error('NEED_REFERRER') // 特殊错误码
        }

        // ✅ 显示确认弹窗
        pendingReferrerCode.value = referrerCode
        showReferrerDialog.value = true

        // 等待用户确认
        const confirmed = await new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            if (!showReferrerDialog.value) {
              clearInterval(checkInterval)
              resolve(true)
            }
          }, 100)

          // 30秒超时
          setTimeout(() => {
            clearInterval(checkInterval)
            showReferrerDialog.value = false
            resolve(false)
          }, 30000)
        })

        if (!confirmed) {
          throw new Error('用户取消了推荐人确认')
        }

      }

      // ethers v5: 使用 Web3Provider
      provider.value = new ethers.providers.Web3Provider(providerRaw)

      // ethers v5: signer 直接从 provider 获取
      signer.value = provider.value.getSigner()

      console.log("wallet connected", address.value)

      await switchToBSC(providerRaw, TARGET_CHAIN_ID)
      chainId.value = parseInt(providerRaw.chainId, 16)
      console.log("chain switched", chainId.value)

      // ✅ 初始化合约
      initContracts()

      // 检查是否已有有效 token
      const existingToken = localStorage.getItem("uk_token")
      const savedAddress = localStorage.getItem("uk_address")
      const registered = localStorage.getItem("uk_registered")


      if (existingToken && savedAddress === address.value && registered) {
        console.log("using existing session, skip signing")
        token.value = existingToken
        isConnected.value = true
        isLoggedIn.value = true
        listenWallet()
        return { token: existingToken, skipSign: true }
      }

      // 需要签名登录
      const nonceRes = await authAPI.getNonce(address.value)
      const message = nonceRes.data.message

      const signature = await providerRaw.request({
        method: "personal_sign",
        params: [message, address.value]
      })

      console.log("signature:", signature)

      localStorage.setItem("signature", signature);

      const loginRes = await authAPI.login({
        wallet_address: address.value,
        signature,
        message,
        invite_code: localStorage.getItem("referrer_code") || ""
      })

      token.value = loginRes.data.token
      invite_code.value = loginRes.data.invite_code
      localStorage.setItem("uk_token", token.value)
      localStorage.setItem("uk_address", address.value)
      localStorage.setItem("uk_invite_code", invite_code.value)
      localStorage.setItem("uk_registered", "true")

      isConnected.value = true // ✅ 添加这一行
      isRegistered.value = true
      isLoggedIn.value = true


      listenWallet()

      return loginRes.data

    } catch (e) {
      console.error("wallet connect error", e)
      reset()
      throw e
    } finally {
      isLoading.value = false
    }
  }

  function listenWallet() {
    const raw = window.ethereum
    if (!raw) return

    raw.on("accountsChanged", (accounts) => {
      if (!accounts.length) {
        disconnect()
        return
      }

      if (accounts[0].toLowerCase() !== address.value) {
        disconnect()
      }
    })

    raw.on("chainChanged", (cid) => {
      chainId.value = parseInt(cid, 16)
      if (chainId.value !== TARGET_CHAIN_ID) {
        switchToBSC(raw, TARGET_CHAIN_ID)
      }
    })

    raw.on("disconnect", () => {
      disconnect()
    })
  }

  function disconnect() {
    reset()
    isLoggedIn.value = false
    // localStorage.removeItem("uk_token")
    // localStorage.removeItem("uk_address")
    // localStorage.removeItem("uk_registered")
  }

  function reset() {
    address.value = ""
    provider.value = null
    signer.value = null
    chainId.value = null
    isConnected.value = false
    token.value = ""
    isRegistered.value = false
  }

  async function restoreSession() {
    const savedToken = localStorage.getItem("uk_token")
    const savedAddress = localStorage.getItem("uk_address")
    const savedRegistered = localStorage.getItem("uk_registered") === true

    if (!savedToken || !window.ethereum) return false

    try {
      const accounts = await window.ethereum.request({
        method: "eth_accounts"
      })

      if (!accounts.length) return false

      const currentAddress = accounts[0].toLowerCase()

      // 地址不匹配，清除旧会话
      if (savedAddress && currentAddress !== savedAddress) {
        console.log("address mismatch, clearing session")
        disconnect()
        return false
      }

      // ethers v5: 使用 Web3Provider
      const providerObj = new ethers.providers.Web3Provider(window.ethereum)

      provider.value = providerObj
      address.value = currentAddress

      // ethers v5: 直接从 provider 获取 signer
      signer.value = providerObj.getSigner()

      chainId.value = parseInt(window.ethereum.chainId, 16)
      token.value = savedToken
      isRegistered.value = savedRegistered
      isConnected.value = true

      localStorage.setItem("uk_address", address.value)

      listenWallet()

      return true

    } catch (e) {
      console.warn("restore session failed", e)
      return false
    }
  }

  function getUKContract() {
    const addr = import.meta.env.VITE_UK_CONTRACT
    if (!signer.value || !addr) return null
    return new ethers.Contract(addr, UK_TOKEN_ABI, signer.value)
  }

  function getUSDTContract() {
    const addr = import.meta.env.VITE_USDT_CONTRACT
    if (!signer.value || !addr) return null
    return new ethers.Contract(addr, USDT_ABI, signer.value)
  }

  function getPredictionContract() {
    const addr = import.meta.env.VITE_PREDICTION_CONTRACT
    if (!signer.value || !addr) return null
    return new ethers.Contract(addr, LIQUIDITY_PREDICTION_ABI, signer.value)
  }


  // 添加权限检查函数
  function checkPermission() {
    if (!address.value) {
      throw new Error('请先连接钱包')
    }

    if (needsReferrer(address.value)&& !isLoggedIn) {
      const referrerCode = localStorage.getItem("referrer_code")
      if (!referrerCode || !/^0x[a-fA-F0-9]{40}$/.test(referrerCode)) {
        throw new Error('需要有效的上级地址才能执行此操作')
      }
    }

    return true
  }

  // ✅ 获取链上UK余额
  async function getOnChainUKBalance() {
    try {
      console.log("1212121212");
      if (!ukContract.value || !address.value) return 0
      console.log("6666666666", address.value, ukContract.value);
      const balance = await ukContract.value.balanceOf(address.value)
      console.log("6666666666", balance);
      let balance1 = parseFloat(formatUnits(balance, 18))
      console.log("6666666666", address.value, balance1);
      return balance1
    } catch (e) {
      console.error('Get UK balance failed:', e)
      return 0
    }
  }

  // ✅ 执行提现
  async function executeWithdraw(requestId, withdrawType, sigData) {
    try {
      const amount = sigData.amount
      const nonce = sigData.nonce
      const deadline = sigData.deadline_unix
      const signature = sigData.signature

      let tx

      if (withdrawType === 1) {
        // 节点UK释放 → NodeContract.withdrawUK
        if (!nodeContract.value) throw new Error('Node contract not initialized')
        tx = await nodeContract.value.withdrawUK(
          amount,
          nonce,
          deadline,
          signature
        )
      } else {
        // 其他 → LiquidityPrediction.withdrawUK
        if (!predictionContract.value) throw new Error('Prediction contract not initialized')

        // 合约中的withdrawType: 1=流动性 2=社区 3=节点分红
        const contractWithdrawType = withdrawType - 1
        console.log("3333333333333",amount,nonce,deadline,contractWithdrawType)

        tx = await predictionContract.value.withdrawUK(
          amount,
          nonce,
          deadline,
          contractWithdrawType,
          signature
        )
      }

      const receipt = await tx.wait()

      console.log("交易情况",receipt)
      console.log('[submitStake] 交易已确认:', {
        hash: receipt.transactionHash,
        blockNumber: receipt.blockNumber,
        status: receipt.status
      })

      // 确认提现完成
      await userAPI.confirmWithdraw({
        request_id: requestId,
        tx_hash: receipt.transactionHash
      })

      return receipt
    } catch (e) {
      console.error('Execute withdraw failed:', e)
      throw new Error(e.message || 'Withdraw execution failed')
    }
  }

  // ✅ 获取链上USDT余额
  async function getOnChainUSDTBalance() {
    try {
      if (!usdtContract.value || !address.value) return 0
      const decimals = await usdtContract.value.decimals()
      const balance = await usdtContract.value.balanceOf(address.value)
      return parseFloat(formatUnits(balance, decimals))
    } catch (e) {
      console.error('Get USDT balance failed:', e)
      return 0
    }
  }

  function getNodeContract() {
    const addr = import.meta.env.VITE_NODE_CONTRACT
    if (!signer.value || !addr) return null
    // 返回合约实例，但注意不要在其他响应式上下文中直接使用它
    return new ethers.Contract(addr, NODE_ABI, signer.value)
  }

   // 处理推荐人确认
   function confirmReferrer() {
    showReferrerDialog.value = false
  }

  // 取消推荐人确认
  function cancelReferrer() {
    showReferrerDialog.value = false
    pendingReferrerCode.value = ""
  }



  return {
    address,
    provider,
    signer,
    chainId,
    token,
    isConnected,
    isLoading,
    isRegistered,
    shortAddress,
    executeWithdraw,
    getOnChainUKBalance,
    getOnChainUSDTBalance,
    getPredictionContract,
    isLoggedIn,
    checkPermission,
    needsReferrer,
    getUKContract,
    getUSDTContract,
    getNodeContract,
    showReferrerDialog,
    pendingReferrerCode,
    confirmReferrer,
    cancelReferrer,
    connect,
    disconnect,
    restoreSession
  }
})